import i18n from 'i18n'

/**
 * Traducción de cadenas NORMALIZADAS que vienen del backend.
 *
 * - board (meal plan) y rate (cancellation policy): catálogo finito con id estable
 *   (id_board 1-5, id_rate 1-2). Se traducen por id contra el namespace `catalog`.
 *   Si no llega el id, se intenta resolver por el label EN (set único y finito).
 *   `defaultValue` = string crudo del backend → nunca rompe ante un id desconocido.
 * - room (normalizado): el backend devuelve `id_room` + `name` (ES, SIEMPRE existe).
 *   La columna `en` de BD se DESCARTÓ del backend: la traducción EN vive en el catálogo
 *   del frontend `rooms:<id_room>` (rooms.json) con fallback `name`. NO se usa `en` del backend.
 *
 * ⚠️ Los valores raw (raw_room / roomNameRaw) NO se traducen: son el texto crudo de la OTA.
 *
 * Nota reactividad: usa el singleton i18n; los componentes que llaman a estos helpers
 * ya se re-renderizan al cambiar idioma (useTranslation en el árbol), así que el valor
 * se reevalúa con el idioma activo.
 */

const BOARD_LABEL_TO_ID = {
    'Room Only': 1,
    'All Inclusive': 2,
    'Bed and Breakfast': 3,
    'Half Board': 4,
    'Full Board': 5,
}

const RATE_LABEL_TO_ID = {
    'Non Refundable': 1,
    'Refundable': 2,
}

const DEVICE_LABEL_TO_ID = {
    'Desktop': 1,
    'Mobile': 2,
    'App': 3,
    'Basic': 4,
}

// Sentinelas que el backend manda como dato cuando no hay habitación/tarifa/board:
// 'Unknown' (fallback de selectores .get('label','Unknown')) y 'Unknown Room' (registro ROOM real).
// Se traducen de forma centralizada aquí para cubrir TODOS los render spots (celdas, tooltips,
// drawers, selectores) de una vez. Devuelve null si el label no es un sentinela.
const UNKNOWN_LABELS = { 'Unknown': 'unknown', 'Unknown Room': 'unknownRoom' }
function _maybeUnknown(label) {
    const k = UNKNOWN_LABELS[label]
    return k ? i18n.t(`catalog:${k}`, { defaultValue: label }) : null
}

// Avisa UNA sola vez por id desconocido (solo en desarrollo) para que un board/rate
// nuevo en BD no pase desapercibido y se añada al catálogo. Nunca rompe la UI.
const _warned = new Set()
function _warnUnknown(kind, id, label) {
    if (process.env.NODE_ENV === 'production') return
    const k = `${kind}:${id}`
    if (_warned.has(k)) return
    _warned.add(k)
    // eslint-disable-next-line no-console
    console.warn(`[i18n catalog] ${kind} id "${id}" sin traducción (label backend: "${label}"). Añádelo a locales/*/catalog.json.`)
}

function _translate(kind, id, fallbackLabel) {
    if (!id) return fallbackLabel || ''
    const key = `catalog:${kind}.${id}`
    const translated = i18n.t(key, { defaultValue: '' })
    if (!translated) {
        // id desconocido → mostramos el label crudo del backend (EN) y avisamos una vez
        _warnUnknown(kind, id, fallbackLabel)
        return fallbackLabel || ''
    }
    return translated
}

export function translateBoard(idBoard, fallbackLabel = '') {
    const unknown = _maybeUnknown(fallbackLabel)
    if (unknown) return unknown
    const id = idBoard || BOARD_LABEL_TO_ID[fallbackLabel]
    return _translate('board', id, fallbackLabel)
}

export function translateRate(idRate, fallbackLabel = '') {
    const unknown = _maybeUnknown(fallbackLabel)
    if (unknown) return unknown
    const id = idRate || RATE_LABEL_TO_ID[fallbackLabel]
    return _translate('rate', id, fallbackLabel)
}

// device = catálogo finito (1=Desktop, 2=Mobile, 3=App, 4=Basic). Igual que board/rate:
// por id, o por label EN si no llega id. defaultValue = label crudo, nunca rompe.
export function translateDevice(idDevice, fallbackLabel = '') {
    const unknown = _maybeUnknown(fallbackLabel)
    if (unknown) return unknown
    const id = idDevice || DEVICE_LABEL_TO_ID[fallbackLabel]
    return _translate('device', id, fallbackLabel)
}

/**
 * Traduce una etiqueta de GRUPO de las tablas Comparison, que puede ser un rate o un board
 * (la comparison es "by cancellation policy" o "by board"). Resuelve por label porque el id
 * de grupo (id_rate / id_board) es ambiguo sin saber la dimensión, pero los sets de labels EN
 * son disjuntos ('Non Refundable' vs 'Bed and Breakfast'). Fallback = label crudo.
 */
export function translateGroupLabel(label) {
    if (label == null || label === '') return label
    const unknown = _maybeUnknown(label)
    if (unknown) return unknown
    if (RATE_LABEL_TO_ID[label] != null) return translateRate(RATE_LABEL_TO_ID[label], label)
    if (BOARD_LABEL_TO_ID[label] != null) return translateBoard(BOARD_LABEL_TO_ID[label], label)
    return label
}

/**
 * Serie de los charts de Parity Index Evolution (Parity Analyzer y BI). El backend manda
 * `dataKey` = `<kpi>_<type>` (pi|co|diff × global|otas|metas|web) y un `label` EN fijo
 * ("Cost Opportunity Otas"). Se traduce por dataKey; fallback = label del backend.
 */
export function translateParitySeries(dataKey, fallbackLabel = '') {
    const [kpi, type] = (dataKey || '').split('_')
    const kpiLabel = i18n.t(`parity:charts.series.${kpi}`, { defaultValue: '' })
    const typeLabel = i18n.t(`parity:charts.series.${type}`, { defaultValue: '' })
    return kpiLabel && typeLabel ? `${kpiLabel} ${typeLabel}` : fallbackLabel
}

const COMPETITION_KPI_SERIES = ['compsetMin', 'compsetAvgMin', 'mainHotelAvgMin']

/**
 * Serie de los charts de Competition (Analyzer y panel master de la Home). El backend manda las
 * series KPI con `dataKey` fijo (compsetMin | compsetAvgMin | mainHotelAvgMin) y `label` EN; se
 * traducen por dataKey. Las series de hotel (label = nombre del hotel) se devuelven tal cual.
 */
export function translateCompetitionSeries(dataKey, fallbackLabel = '') {
    if (!COMPETITION_KPI_SERIES.includes(dataKey)) return fallbackLabel
    return i18n.t(`competition:chart.series.${dataKey}`, { defaultValue: fallbackLabel })
}

// Language of ROOM.name sent by the backend: rooms need no catalog in this language.
export const ROOM_SOURCE_LANGUAGE = 'es'

/**
 * Room normalizado (id_room). El backend devuelve `id_room` + `name` (ES, SIEMPRE existe).
 * - ROOM_SOURCE_LANGUAGE (es) → `name` directamente.
 * - Cualquier otro idioma → catálogo `rooms:<id_room>` de ese idioma (src/locales/<lang>/rooms.json;
 *   EN sembrado de la columna `en` de BD). Si falta → fallback i18next a EN → `name`. Nunca vacío.
 * NO traduce raw rooms (raw_room): se dejan tal cual.
 */
export function localizedRoom({ id_room, name } = {}) {
    const fallback = name || ''
    // Sentinela del backend cuando no hay habitación ("Unknown Room") → traducir en todos los idiomas.
    const unknown = _maybeUnknown(fallback)
    if (unknown) return unknown
    const lang = (i18n.language || 'en').slice(0, 2)
    if (lang === ROOM_SOURCE_LANGUAGE) return fallback
    if (id_room == null) return fallback
    return i18n.t(`rooms:${id_room}`, { defaultValue: fallback })
}
